'use client';
import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '../cn';

export function Chip({ children, onRemove, className, tone = 'gray' }: { children: React.ReactNode; onRemove?: () => void; className?: string; tone?: 'gray' | 'accent' }) {
  return (
    <span className={cn('inline-flex items-center gap-1 h-6 pl-2.5 rounded-full text-[12px] font-medium', onRemove ? 'pr-1' : 'pr-2.5', tone === 'accent' ? 'bg-accent-tint text-accent' : 'bg-fill-2 text-text', className)}>
      {children}
      {onRemove ? (
        <button type="button" aria-label="Remove" onClick={onRemove} className="rounded-full p-0.5 hover:bg-fill text-current/70 hover:text-current">
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

export interface ChipInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  className?: string;
  id?: string;
}
/** Free-text tag input: Enter or comma adds, Backspace removes the last. */
export function ChipInput({ value, onChange, placeholder = 'Add…', suggestions = [], className, id }: ChipInputProps) {
  const [draft, setDraft] = React.useState('');
  const add = (v: string) => {
    const t = v.trim();
    if (!t || value.includes(t)) return setDraft('');
    onChange([...value, t]);
    setDraft('');
  };
  const filtered = suggestions.filter((s) => !value.includes(s) && s.toLowerCase().includes(draft.toLowerCase())).slice(0, 6);
  return (
    <div className={cn('relative', className)}>
      <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-[var(--radius-sm)] bg-fill-3 px-2 py-1.5 focus-within:bg-surface focus-within:shadow-[0_0_0_3px_var(--accent-ring),0_0_0_1px_var(--accent)] transition-[background-color,box-shadow]">
        {value.map((v) => (
          <Chip key={v} onRemove={() => onChange(value.filter((x) => x !== v))}>
            {v}
          </Chip>
        ))}
        <input
          id={id}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              add(draft);
            } else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => draft && add(draft)}
          placeholder={value.length ? '' : placeholder}
          className="min-w-[80px] flex-1 bg-transparent text-[14px] outline-none placeholder:text-text-3"
        />
      </div>
      {draft && filtered.length ? (
        <div className="absolute z-30 mt-1 w-full rounded-[var(--radius)] bg-surface p-1 shadow-[var(--shadow-lg)]">
          {filtered.map((s) => (
            <button key={s} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(s)} className="block w-full rounded-[var(--radius-sm)] px-2.5 py-1.5 text-left text-[14px] hover:bg-accent hover:text-white">
              {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
